/**
 * Wallet layer on viem: EIP-6963 injected wallet discovery, chain checking/switching, and a clearly labelled
 * disposable Anvil development key for LOCAL CHAIN mode only. A pending connection never shows a fabricated address,
 * and every failure becomes a typed WalletError the UI can explain inline (no alert dialogs).
 */
import { createWalletClient, custom, http, type Address, type Chain, type EIP1193Provider, type WalletClient } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';

export interface InjectedInfo {
  uuid: string;
  name: string;
  icon: string;
  rdns: string;
  provider: EIP1193Provider;
}

export type WalletErrorCode = 'no-wallet' | 'rejected' | 'wrong-chain' | 'no-account' | 'insufficient-gas' | 'failed';

export class WalletError extends Error {
  constructor(
    readonly code: WalletErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'WalletError';
  }
}

const discovered = new Map<string, InjectedInfo>();
let listening = false;

export function discoverWallets(onChange?: () => void): InjectedInfo[] {
  if (typeof window === 'undefined') return [];
  if (!listening) {
    listening = true;
    window.addEventListener('eip6963:announceProvider', ((e: CustomEvent) => {
      const d = e.detail as { info: Omit<InjectedInfo, 'provider'>; provider: EIP1193Provider };
      if (!d?.info?.uuid || !d.provider) return;
      discovered.set(d.info.uuid, { ...d.info, provider: d.provider });
      onChange?.();
    }) as EventListener);
  }
  window.dispatchEvent(new Event('eip6963:requestProvider'));
  const legacy = (window as unknown as { ethereum?: EIP1193Provider }).ethereum;
  if (legacy && discovered.size === 0) {
    discovered.set('legacy', { uuid: 'legacy', name: 'Browser wallet', icon: '', rdns: 'legacy', provider: legacy });
  }
  return [...discovered.values()];
}

export type ConnectedWallet =
  | { kind: 'dev'; address: Address; client: WalletClient; label: string; chainId: number }
  | { kind: 'injected'; address: Address; client: WalletClient; label: string; provider: EIP1193Provider; chainId: number; name: string };

/** Anvil account #5 — public, disposable development key. Never valid on any other chain. */
const DEV_KEY = '0x8b3a350cf5c34c9194ca85829a2df0ec3153be0318b5e2d3348e872092edffba' as const;

export function devWallet(chain: Chain, rpcUrl: string): ConnectedWallet {
  if (chain.id !== 31337) throw new WalletError('wrong-chain', 'The development key is only allowed on the local chain (31337).');
  const account = privateKeyToAccount(DEV_KEY);
  return {
    kind: 'dev',
    address: account.address,
    client: createWalletClient({ account, chain, transport: http(rpcUrl) }),
    label: 'Local dev key #5 (DISPOSABLE — Anvil public key)',
    chainId: chain.id,
  };
}

/** True when the error is the wallet's "user rejected" (EIP-1193 4001) in any of its common shapes. */
export function isRejection(err: unknown): boolean {
  const e = err as { code?: number; cause?: { code?: number }; name?: string; message?: string; shortMessage?: string };
  if (e?.code === 4001 || e?.cause?.code === 4001) return true;
  if (e?.name === 'UserRejectedRequestError') return true;
  return /user rejected|user denied|rejected the request|denied transaction/i.test(`${e?.shortMessage ?? ''} ${e?.message ?? ''}`);
}

export async function walletChainId(provider: EIP1193Provider): Promise<number> {
  const hex = (await provider.request({ method: 'eth_chainId' })) as string;
  return parseInt(hex, 16);
}

/** Ask the wallet to switch (adding the local chain when it does not know it). Returns the wallet's chain afterwards. */
export async function switchWalletChain(provider: EIP1193Provider, chain: Chain, rpcUrl: string): Promise<number> {
  const hex = `0x${chain.id.toString(16)}` as const;
  try {
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] });
  } catch (err) {
    const code = (err as { code?: number }).code;
    if (code === 4902) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [{ chainId: hex, chainName: chain.name, nativeCurrency: chain.nativeCurrency, rpcUrls: [rpcUrl], ...(chain.blockExplorers ? { blockExplorerUrls: [chain.blockExplorers.default.url] } : {}) }],
      });
    } else if (isRejection(err)) {
      throw new WalletError('wrong-chain', `Network switch rejected. The wallet stays on chain ${await walletChainId(provider).catch(() => '?')}; RUNNER needs chain ${chain.id}.`);
    } else {
      throw new WalletError('wrong-chain', `The wallet could not switch to chain ${chain.id}: ${(err as Error).message.split('\n')[0]}`);
    }
  }
  return walletChainId(provider);
}

/**
 * Connect an injected wallet. A wallet on another chain is still connected (so the UI can show who is connected),
 * but `chainId` tells the caller it is on the wrong network; transactions are blocked until it switches.
 */
export async function connectInjected(w: InjectedInfo, chain: Chain, rpcUrl: string): Promise<ConnectedWallet> {
  let accounts: Address[];
  try {
    accounts = (await w.provider.request({ method: 'eth_requestAccounts' })) as Address[];
  } catch (err) {
    if (isRejection(err)) throw new WalletError('rejected', `Connection request rejected in ${w.name}. Nothing was connected.`);
    throw new WalletError('failed', `${w.name} could not connect: ${(err as Error).message.split('\n')[0]}`);
  }
  if (!accounts?.length) throw new WalletError('no-account', `${w.name} did not authorize any account.`);
  let chainId = await walletChainId(w.provider);
  if (chainId !== chain.id) {
    try {
      chainId = await switchWalletChain(w.provider, chain, rpcUrl);
    } catch {
      chainId = await walletChainId(w.provider).catch(() => chainId);
    }
  }
  return {
    kind: 'injected',
    address: accounts[0],
    client: createWalletClient({ account: accounts[0], chain, transport: custom(w.provider) }),
    label: `${accounts[0].slice(0, 6)}…${accounts[0].slice(-4)} · ${w.name}`,
    provider: w.provider,
    chainId,
    name: w.name,
  };
}
